import React, { useContext, useEffect, useRef } from "react";
import { store } from "../../../Store";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";

import { ModoPWAContext } from "../../../../contexts/ModoPwaContext";
import { useLogout } from "../../../../hooks/useLogout";
import useIsMobile from "../../../../js/utilities/useIsMobile";

import { useGeneralHeaderDropdown } from "../../../../hooks/header/useGeneralHeaderDropdown";
import { useNetworkStatus } from "../../../../hooks/use-network-status";
import { DropdownNotificationButton } from "../../components/notification-button";
import { useAccessibility } from "../../../../hooks/useAccessibility";
import { MenuLanguageSelector } from "../../../language/languageSelector";
import { usePersonalAccountPermissions } from "../../../../hooks/use-personal-account-permissions";
import { FocusScope } from "react-aria";
import { useId } from "../../../../hooks/use-id";

/**
 * Componente con las opciones favoritos, descargas, mi panel y cerrar sesión
 * @param {object} textosInterfaz - contiene los textos de interfaz
 * @returns  {JSX.Element} DropdownMiCuenta
 */
export const DropdownMiCuenta = ({ textosInterfaz }) => {
    const pwaState = useContext(ModoPWAContext);
    const globalState = useContext(store);
    const { state } = globalState;
    const location = useLocation();
    const accessibilityButtonRef = useRef(null);
    const logoutHandler = useLogout();
    const isMobile = useIsMobile();
    const networkStatus = useNetworkStatus();
    const isPersonalAccount = state?.isPersonalAccount;
    const accessibility = useAccessibility();
    const personalAccountPermissions = usePersonalAccountPermissions();
    const canViewProfile = personalAccountPermissions.hasPermission('view');
    const canEditProfile = personalAccountPermissions.hasPermission('edit');
    const canCreateProfile = personalAccountPermissions.hasPermission('create');
    const isAlternativeFlow = personalAccountPermissions.hasPermission('alternative-association-flow');
    const personalAccountName = state?.personalAccount?.fullname || 'User';

    const ref = useRef(null);
    const showADropdown = useGeneralHeaderDropdown();
    const visible = showADropdown?.state?.visible;
    const triggerButton = globalState?.state?.generalHeaderDropdown?.triggerButtonRef;

    const menuId = useId("user-menu");

    useEffect(() => {
        window.addEventListener("scroll", () => {
            if (window.scrollY >= 1 && window.scrollY < 10) {
                showADropdown.setVisible(false);
            }
        });
    }, [visible]);

    /**Función que permite cerrar el dropdown dando click fuera de él
    * @param {event} event 
    */
    const handleOutsideClick = (event) => {
        if (triggerButton?.current?.contains(event.target)) return;
        if (ref.current && !ref.current.contains(event.target)) {
            showADropdown.setVisible(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [triggerButton]);

    /*Asigna el botón que abre las opciones de accesibilidad */
    useEffect(() => {
        if (accessibilityButtonRef.current) {
            globalState.dispatch({
                type: "CHANGE",
                path: "accessibility.triggerButton",
                value: accessibilityButtonRef,
            });
        }
    }, [accessibilityButtonRef]);

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            showADropdown.setVisible(false);
        }
    };

    return (
        <FocusScope contain restoreFocus autoFocus>
            <div className={`${isMobile ? 'dropdownMenuResponsive' : 'dropdownMenu'} ${visible ? 'activeMode' : 'inactiveMode'}`} onKeyDown={handleKeyDown}>
                <ul ref={ref} role="menu" id={menuId} aria-label="Menú de usuario">
                    {/* {isPersonalAccount && canViewProfile && canEditProfile && (
                        <li className={`${visible ? 'activeMode' : 'inactiveMode'} d-flex align-items-center justify-content-center`} role="none">
                            <Link
                                to="/perfil"
                                onClick={() => showADropdown.setVisible(false)}
                                className="link-principal d-flex justify-content-center align-items-center focusable-red justify-content-center"
                                role="menuitem"
                            >
                                <Icon icon="fluent:person-32-filled" width={20} className="mr-2" />
                                <span

                                    style={{
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        display: 'block',
                                        width: 'fit-content'
                                    }}
                                    title={personalAccountName}
                                >
                                    {personalAccountName}
                                </span>
                            </Link>
                        </li>
                    )}

                    {isPersonalAccount && canViewProfile && (
                        <li className={`${visible ? 'activeMode' : 'inactiveMode'} d-flex align-items-center justify-content-center`} role="none">
                            <Link
                                to="/perfil/institucion"
                                onClick={() => showADropdown.setVisible(false)}
                                className="link-principal d-flex justify-content-center align-items-center focusable-red justify-content-center"
                                role="menuitem"
                            >
                                <Icon icon="tdesign:institution-filled" width={20} className="mr-2" />
                                <span

                                    style={{
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        display: 'block',
                                        width:'fit-content'
                                    }}
                                    title={textosInterfaz?.institucion || 'Instituciones'} 
                                >
                                    {textosInterfaz?.institucion || 'Instituciones'}
                                </span>
                            </Link>
                        </li>
                    )}

                    {!isPersonalAccount && canCreateProfile &&
                        <li className={`${visible ? 'activeMode' : 'inactiveMode'}`} role="none">
                            <Link
                                to="/crear-perfil"
                                onClick={() => showADropdown.setVisible(false)}
                                className="link-principal d-flex justify-content-center align-items-center focusable-red"
                                role="menuitem"
                            >
                                <Icon icon="mdi:register" width={20} className="mr-3" />
                                {textosInterfaz?.crear_perfil || 'Crear perfil'}
                            </Link>
                        </li>
                    }
                    {!isPersonalAccount && isAlternativeFlow &&
                        <li className={`${visible ? 'activeMode' : 'inactiveMode'}`} role="none">
                            <Link
                                to="/asociar-institucion-perfil"
                                onClick={() => showADropdown.setVisible(false)}
                                className="link-principal d-flex justify-content-center align-items-center focusable-red"
                                role="menuitem"
                            >
                                <svg
                                    className="mr-3" 
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M15 2.458C14.0291 2.1536 13.0175 1.99916 12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22C17.523 22 22 17.523 22 12C22.0008 10.9825 21.8464 9.97088 21.542 9"
										stroke="currentColor"
						                strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M5.5 19.5L6.06 18.52C6.4973 17.7544 7.12926 17.1181 7.8918 16.6756C8.65434 16.233 9.52034 15.9999 10.402 16H13.598C14.4795 16.0001 15.3453 16.2333 16.1076 16.6758C16.87 17.1184 17.5018 17.7546 17.939 18.52L18.499 19.5M15 10C15 10.7956 14.6839 11.5587 14.1213 12.1213C13.5587 12.6839 12.7956 13 12 13C11.2044 13 10.4413 12.6839 9.87868 12.1213C9.31607 11.5587 9 10.7956 9 10C9 9.20435 9.31607 8.44129 9.87868 7.87868C10.4413 7.31607 11.2044 7 12 7C12.7956 7 13.5587 7.31607 14.1213 7.87868C14.6839 8.44129 15 9.20435 15 10ZM18.974 2.021C18.98 1.993 19.02 1.993 19.026 2.021C19.1749 2.74834 19.5341 3.41595 20.0591 3.94092C20.5841 4.46589 21.2517 4.82512 21.979 4.974C22.007 4.98 22.007 5.02 21.979 5.026C21.2517 5.17488 20.5841 5.53411 20.0591 6.05908C19.5341 6.58405 19.1749 7.25166 19.026 7.979C19.02 8.007 18.98 8.007 18.974 7.979C18.8251 7.25166 18.4659 6.58405 17.9409 6.05908C17.4159 5.53411 16.7483 5.17488 16.021 5.026C15.993 5.02 15.993 4.98 16.021 4.974C16.7483 4.82512 17.4159 4.46589 17.9409 3.94092C18.4659 3.41595 18.8251 2.74834 18.974 2.021Z"
										stroke="currentColor"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
                                {textosInterfaz?.asociar_perfil_flujo_alternativo || 'Asociar perfil'}
                            </Link>
                        </li>
                    } */}
                    {
                        pwaState?.clienteHabilitado && pwaState?.compatible &&

                        <li className={`${visible ? 'activeMode' : 'inactiveMode'}`} role="none">
                            <Link
                                to="/descargados"
                                onClick={() => showADropdown.setVisible(false)}
                                className="link-principal d-flex justify-content-center align-items-center focusable-red"
                                role="menuitem"
                            >
                                <Icon icon="ri:download-line" width={20} className="mr-3" />
                                {textosInterfaz?.descargas || 'Descargas'}
                            </Link>
                        </li>
                    }

                    <li className={`${visible ? 'activeMode' : 'inactiveMode'}`} role="none">
                        <Link
                            to="/favoritos"
                            onClick={() => showADropdown.setVisible(false)}
                            className="link-principal d-flex justify-content-center align-items-center focusable-red"
                            role="menuitem"
                        >
                            <Icon icon="material-symbols:favorite" width={20} className="mr-3" />
                            {textosInterfaz?.favoritos || 'Favoritos'}
                        </Link>
                    </li>

                    {isMobile &&
                        <li className={`${visible ? 'activeMode' : 'inactiveMode'}`} role="none">
                            <Link
                                to="/club-makemake"
                                onClick={() => showADropdown.setVisible(false)}
                                className="link-principal d-flex justify-content-center align-items-center focusable-red"
                                role="menuitem"
                            >
                                <Icon icon="iconoir:community" width={20} className="mr-3" />
                                {textosInterfaz?.comunidad || 'Comunidad'}
                            </Link>
                        </li>
                    }

                    {state.userType === 'Cliente' && !isPersonalAccount &&
                        <li className={`${visible ? 'activeMode' : 'inactiveMode'}`} role="none">
                            <Link
                                to={"/mi-panel/mi-cuenta"}
                                onClick={() => showADropdown.setVisible(false)}
                                className="link-principal d-flex justify-content-center align-items-center focusable-red"
                                role="menuitem"
                            >
                                <Icon icon="mage:dashboard-chart" width={20} className="mr-3" />
                                {textosInterfaz?.panel || 'Mi panel'}
                            </Link>
                        </li>
                    }

                    {!isMobile &&
                        <li className={`${visible ? 'activeMode' : 'inactiveMode'}`} role="none">
                            <Link
                                to="/ayuda"
                                onClick={() => showADropdown.setVisible(false)}
                                className="link-principal d-flex justify-content-center align-items-center focusable-red"
                                role="menuitem"
                            >
                                <Icon icon="carbon:help" width={20} className="mr-3" />
                                {textosInterfaz?.ayuda || 'Ayuda'}
                            </Link>
                        </li>
                    }

                    {isMobile &&
                        <>
                            <hr className={`${visible ? 'activeMode' : 'inactiveMode'}`} role="separator" />

                            <DropdownNotificationButton
                                visibleDropdown={visible}
                                onClick={() => showADropdown.setVisible(false)}
                                translations={textosInterfaz} />

                            <li className={`${visible ? 'activeMode' : 'inactiveMode'}`} role="none" >
                                <button
                                    ref={accessibilityButtonRef}
                                    className="link-secundario focusable-red mx-auto w-100"
                                    style={{ fontWeight: 500 }}
                                    onClick={() => {
                                        accessibility.setVisible(true);
                                        showADropdown.setVisible(false)
                                    }}
                                    role="menuitem"
                                >
                                    {textosInterfaz?.accesibilidad || 'Accesibilidad'}
                                </button>
                            </li>
                            <hr className={`${visible ? 'activeMode' : 'inactiveMode'}`} role="separator" />
                        </>

                    }

                    {(!pwaState.appInstalada && networkStatus.online === true) &&
                        <li className={`${visible ? 'activeMode' : 'inactiveMode'}`} role="none">
                            <button
                                tabIndex={0}
                                className="link-secundario w-100 d-flex justify-content-center align-items-center focusable-red"
                                onClick={(e) => {
                                    e.preventDefault();
                                    logoutHandler.logout();
                                    showADropdown.setVisible(false)
                                }}
                            >
                                <span role="status" aria-live="polite">
                                    {logoutHandler?.loading ?
                                        'Cerrando sesión...'
                                        :
                                        textosInterfaz?.cerrar_sesion || 'Cerrar sesión'
                                    }
                                </span>
                            </button>
                            {isMobile &&
                                <div className="col-sm-12 mt-3 d-flex justify-content-center aling-items-center">
                                    <MenuLanguageSelector />
                                </div>
                            }
                        </li>
                    }

                    <li className={`close-options ${visible ? 'activeMode' : 'inactiveMode'}`}>
                        <button onClick={() => showADropdown.setVisible(false)} aria-label={textosInterfaz?.button_close_list_alt || "Cerrar la lista desplegable"} className="w-100 focusable-red ">
                            {textosInterfaz?.button_close_list || "Cerrar lista"}
                        </button>
                    </li>

                </ul>
            </div >
        </FocusScope>
    )
}